

let __elakayInfoSource;
const __elakayText = (key, fallback) =>
  __elakayInfoSource?.laguangeElakay?.("frontElakayHardcodedText", __elakayInfoSource.languageAPI)?.["elakayutils"]?.[key] || fallback;

// elakay-utils.js


/**
 * 1) "Gal" -> Matrice [ [names], [images] ] uniques
 * @param {{ Gal: Array<{name:string, images:string[]}> }} data
 * @returns {[string[], string[]]}
 */
export function extract_Na_Ing_galeries(data) {
  if (!data || !Array.isArray(data)) return [[], []];

  const names = [...new Set(data?.map(item => item.name))];
  const images = [...new Set(
    data.flatMap(item => Array.isArray(item.images) ? item.images : [])
  )];

  return {names, images};
}

/**
 * 2) "filPro" -> Matrice [ [filterKeys], [filterValues] ] uniques
 * @param {{ filPro: Array<{ filter: Record<string,string> }> }} data
 * @returns {[string[], string[]]}
 */
export function extract_K_V_filterProduct(data) {
  if (!data || !Array.isArray(data)) return [[], []];

  const keys = new Set();
  const values = new Set();

  data.forEach(item => {
    if (item && item.filter && typeof item.filter === "object") {
      Object.entries(item.filter).forEach(([k, v]) => {
        keys.add(k);
        values.add(v);
      });
    }
  });

  return {keys: [...keys], values: [...values]};
}

/**
 * 3) "Fil" -> contrôle simple
 *    Retourne { error, urls } :
 *    - error: true si au moins une valeur de FilterItem n'a pas sa ligne dans Images
 *    - urls: toutes les URLs uniques trouvées dans Images (toutes sections confondues)
 * @param {{ Fil: Array<{ FilterItem:string[], Images:(string[]|object)[] }> }} data
 */


export function checkFilterImages(data) {
  if (!data) {
    return {
      error: false,
      urls: [],
      filter_url: [],
      filter_item: [],
      missing: []
    };
  }

  // 🔹 Normalisation : objet → tableau
  const normalizedData = Array.isArray(data) ? data : Object.values(data);
  if (!Array.isArray(normalizedData)) {
    return {
      error: false,
      urls: [],
      filter_url: [],
      filter_item: [],
      missing: []
    };
  }

  let hasError = false;
  const allUrls = [];
  const allFilterItems = new Set();
  const missingKeys = new Set();
  const filterUrlAligned = [];

  normalizedData.forEach(item => {
    // 🔸 Récupère les FilterItem
    const filterItems = Array.isArray(item?.FilterItem)
      ? item.FilterItem
      : item?.FilterItem && typeof item.FilterItem === "object"
      ? Object.values(item.FilterItem)
      : [];

    // 🔸 Récupère les paires Images
    const imagePairs = Array.isArray(item?.Images)
      ? item.Images
      : item?.Images && typeof item.Images === "object"
      ? Object.entries(item.Images)
      : [];

    // 🔸 Ajoute les FilterItems au set global
    filterItems.forEach(f => allFilterItems.add(f));

    imagePairs.forEach(entry => {
      const key = Array.isArray(entry) ? entry[0] : entry?.key;
      const url = Array.isArray(entry) ? entry[1] : entry?.value;

      if (url) {
        allUrls.push(url);
        filterUrlAligned.push(key); // même index pour clé et URL
      }

      if (!filterItems.includes(key)) {
        missingKeys.add(key);
        hasError = true;
      }
    });
  });

  return {
    error: hasError,
    missing: [...missingKeys].filter(Boolean),
    urls: allUrls,
    filter_url: filterUrlAligned,
    filter_item: [...allFilterItems]
  };
}



/**
 * 4) "GalID" -> tableau des URLs uniques depuis galeries[].urlImg
 * @param {{ GalID:{ galeries: Array<{urlImg:string}> } }} data
 * @returns {string[]}
 */
export function extract_Urls_galeriesID0(data) {
  if (!data || !data || !Array.isArray(data.galeries)) return [];
  const urls = data.galeries
    ?.map(i => i?.urlImg)
    .filter(u => typeof u === "string" && u.trim() !== "");
  return [...new Set(urls)];
}
/**
 * 4) "GalID" -> tableau des URLs uniques depuis galeries[].urlImg
 * Compatible avec les structures où urlImg est soit string, soit string[]
 * @param {{ galeriesID?: { galeries?: Array<{ urlImg: string | string[] }> } }} data
 * @returns {string[]}
 */
export function extract_Urls_galeriesID(data) {
  if (!data || !data || !Array.isArray(data.galeries)) return [];

  const urls = data.galeries.flatMap(item => {
    if (Array.isArray(item.urlImg)) {
      return item.urlImg.filter(u => typeof u === "string" && u.trim() !== "");
    } else if (typeof item.urlImg === "string" && item.urlImg.trim() !== "") {
      return [item.urlImg];
    }
    return [];
  });

  return [...new Set(urls)];
}

// A product-hosted "/elakay_api/img/:productID/:galeryID" copy (see
// galeriesProduct.js) -- as opposed to a category's /pubCi|pubR route or an
// external supplier CDN URL (CJ dropshipping, etc). Only ever used to prefer
// Elakay's own copy when one exists alongside a supplier URL for the same
// photo, never to decide whether a URL is displayable (both are).
export const isElakayProductImageUrl = (url) =>
  typeof url === "string" && /\/elakay_api\/img\//i.test(url);

// Stable partition: Elakay-hosted copies first, preserving relative order
// within each group. A dropshipping product's ProductImageUrls can mix a
// local Elakay copy (imported via cjImageImport.service.js) with the
// supplier's own CDN URL for the same photo -- prefer serving Elakay's own
// copy so display never depends on a third-party host staying up.
const prioritizeLocalElakayImages = (list) => {
  if (!Array.isArray(list)) return list;
  const local = [];
  const rest = [];
  list.forEach((url) => (isElakayProductImageUrl(url) ? local : rest).push(url));
  return [...local, ...rest];
};

/**
 * Retourne toutes les images utilisables d'un produit, sans doublons.
 * ProductImageUrls est la source canonique ; les anciens champs restent des
 * fallbacks pour les produits qui n'ont pas encore été resynchronisés.
 */
export function getProductImageUrls(product, limit = Infinity) {
  if (!product || typeof product !== "object") return [];
  const urls = new Set();
  const add = (value) => {
    if (urls.size >= limit || value == null) return;
    if (Array.isArray(value)) {
      value.forEach(add);
      return;
    }
    if (typeof value === "string") {
      const url = value.trim();
      if (url) urls.add(url);
      return;
    }
    if (typeof value === "object") {
      add(value.urlImg ?? value.url ?? value.src ?? value.image ?? value.images);
    }
  };

  add(prioritizeLocalElakayImages(product.ProductImageUrls));
  (product.galeries || []).forEach((gallery) => add(gallery?.images ?? gallery?.urlImg));
  (product.filters || []).forEach((filter) => {
    const images = Array.isArray(filter?.Images)
      ? filter.Images
      : Object.entries(filter?.Images || {});
    images.forEach((entry) => add(Array.isArray(entry) && entry.length >= 2 ? entry[1] : entry));
  });
  (product.filterProduct || []).forEach((filter) => add(filter?.images ?? filter?.Images));
  add(product.galeriesID?.galeries);
  add(product.dropshippingData?.images);
  add(product.customerDropshipping?.images);

  return [...urls].slice(0, limit);
}

export const getProductPrimaryImage = (product, fallback = "") =>
  getProductImageUrls(product, 1)[0] || fallback;

/**
 * Supprime le paramètre "?bgc=..." d'une URL
 * @param {string} url
 * @returns {string}
 */
function removeBGCParam(url) {
  if (typeof url !== "string") return url;
  const index = url.indexOf("?bgc=");
  return index !== -1 ? url.slice(0, index) : url;
}

/**
 * 5) Comparer deux tableaux : true si arr2 ⊆ arr1, sinon false.
 *    Ignore les différences dues au paramètre "?bgc=" dans les URLs.
 * @param {string[]} arr1
 * @param {string[]} arr2
 * @returns {boolean}
 */
export function compareArrays(arr1, arr2) {
  // 🔹 Vérifie que ce sont bien des tableaux
  if (!Array.isArray(arr1) || !Array.isArray(arr2)) {
    return { error: false, missing: [], message: __elakayText("t001", "Invalid input") };
  }

  // 🔹 Nettoie les deux tableaux avec removeBGCParam si défini
  const clean1 = arr1?.map(el => removeBGCParam(el));
  const clean2 = arr2?.map(el => removeBGCParam(el));

  // 🔹 Trouve les éléments de arr2 absents dans arr1
  const missing = clean2.filter(el => !clean1.includes(el));

  // 🔹 Retourne le résultat complet
  return {
    error: missing?.length > 0,
    missing, // ex: ['white', 'bluee']
    arr1: clean1,
    arr2: clean2
  };
}

/**
 * 6) Valider un produit contre un "findArgs" embarqué.
 *    Retourne true si toutes les conditions passent, sinon false.
 *    NOTE: { $ne: null } traite aussi "", undefined comme "vides" => échec.
 * @param {object} doc
 * @returns {boolean}
 */

// export function validateProductData(doc) {
//   const findArgs = {
//     categoriesID: { $exists: true, $not: { $size: 0 } },
//     "categoriesID.catID": { $ne: null },
//     categoriesID: { $elemMatch: { gategoriesLink: { $exists: true, $not: { $size: 0 } } } },
//     owner: { $ne: null },
//     description: { $ne: null },
//     name: { $ne: null },
//     filterProduct: { $exists: true, $not: { $size: 0 } },
//     galeriesID: { $ne: null },
//     addressShop: { $ne: null },
//     condition: { $ne: null },
//     "sellActive.sell": true,
//     price: { $gt: 0 }
//   };

//   const isEmpty = (v) => v === null || v === undefined || v === "";
//   const isPlainObject = (o) => o && typeof o === "object" && !Array.isArray(o);
//   const isOperatorKey = (k) => k.startsWith("$");

//   function getDeepValue(obj, path) {
//     return path.split(".").reduce((acc, key) => (acc != null ? acc[key] : undefined), obj);
//   }

//   function testCondition(value, condition) {
//     if (!isPlainObject(condition)) {
//       return value === condition;
//     }

//     // $exists
//     if ("$exists" in condition) {
//       const exists = value !== undefined;
//       if (condition.$exists && !exists) return false;
//       if (!condition.$exists && exists) return false;
//     }

//     // $ne (vide = échec si $ne === null)
//     if ("$ne" in condition) {
//       if (condition.$ne === null) {
//         if (isEmpty(value)) return false; // rejette null/undefined/""
//       } else {
//         if (value === condition.$ne) return false;
//       }
//     }

//     // $gt
//     if ("$gt" in condition) {
//       if (!(typeof value === "number" && value > condition.$gt)) return false;
//     }

//     // $size
//     if ("$size" in condition) {
//       if (!Array.isArray(value) || value?.length !== condition.$size) return false;
//     }

//     // $not.$size
//     if ("$not" in condition && isPlainObject(condition.$not)) {
//       if ("$size" in condition.$not) {
//         if (Array.isArray(value) && value?.length === condition.$not.$size) return false;
//       }
//     }

//     // $elemMatch
//     if ("$elemMatch" in condition) {
//       const sub = condition.$elemMatch;
//       if (!Array.isArray(value)) return false;

//       const anyMatch = value.some((el) => {
//         if (!isPlainObject(sub)) return testCondition(el, sub);
//         for (const [k, v] of Object.entries(sub)) {
//           if (isOperatorKey(k)) {
//             if (!testCondition(el, { [k]: v })) return false;
//           } else {
//             const subVal = el?.[k];
//             if (!testCondition(subVal, v)) return false;
//           }
//         }
//         return true;
//       });

//       if (!anyMatch) return false;
//     }

//     // Sous-champs non opérateurs
//     for (const [k, v] of Object.entries(condition)) {
//       if (!isOperatorKey(k)) {
//         const subVal = value?.[k];
//         if (!testCondition(subVal, v)) return false;
//       }
//     }

//     return true;
//   }

//   for (const [path, condition] of Object.entries(findArgs)) {
//     const val = getDeepValue(doc, path);
//     if (!testCondition(val, condition)) return false;
//   }
//   return true;
// }
export function validateProductData(doc) {
  // 🔹 Conditions à vérifier (incluses dans la fonction)
  const findArgs = {
    "categoriesID.catID": { $ne: null },
    categoriesID: { $elemMatch: { gategoriesLink: { $exists: true, $not: { $size: 0 } } } },
    owner: { $ne: null },
    description: { $ne: null },
    name: { $ne: null },
    filterProduct: { $exists: true, $not: { $size: 0 } },
    galeriesID: { $ne: null },
    addressShop: { $ne: null },
    condition: { $ne: null },
    "sellActive.sell": true,
    price: { $gt: 0 }
  };

  // 🔹 Fonction pour récupérer les valeurs imbriquées ("a.b.c")
  function getDeepValue(obj, path) {
    return path.split(".").reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
  }

  // 🔹 Fonction pour évaluer les conditions MongoDB
  function testCondition(value, condition) {
    if (condition && typeof condition === "object" && !Array.isArray(condition)) {
      if ("$exists" in condition) {
        const exists = value !== undefined && value !== null;
        if (condition.$exists && !exists) return false;
        if (!condition.$exists && exists) return false;
      }
      if ("$ne" in condition && value === condition.$ne) return false;
      if ("$gt" in condition && !(typeof value === "number" && value > condition.$gt)) return false;
      if ("$not" in condition && "$size" in condition.$not) {
        if (Array.isArray(value) && value?.length === condition.$not.$size) return false;
      }
      if ("$size" in condition && Array.isArray(value) && value?.length !== condition.$size) return false;
      if ("$elemMatch" in condition) {
        if (!Array.isArray(value) || !value.some(v => testCondition(v, condition.$elemMatch))) return false;
      }
    } else {
      if (value !== condition) return false;
    }
    return true;
  }

  // 🔹 Parcours de toutes les conditions
  for (const [key, condition] of Object.entries(findArgs)) {
    const value = getDeepValue(doc, key);
    if (!testCondition(value, condition)) {
      // 🚫 retourne false dès qu'une condition échoue
      return false;
    }
  }

  // ✅ toutes les conditions respectées
  return true;
}

/* -----------------------------------------------------------
 * Optionnel: une petite page de démo React (facultatif)
 * -----------------------------------------------------------
 * Place ceci dans src/pages/ElakayUtilsDemo.jsx si tu veux visualiser/essayer rapidement.
 *
 * import React from "react";
 * import {
 *   extractUniqueMatrix,
 *   extractFilterMatrix,
 *   checkFilterImages,
 *   extractUniqueUrls,
 *   compareArrays,
 *   validateProductData
 * } from "../lib/elakay-utils";
 


/**
 * 7) Sélectionner une URL selon la cohérence galeriesID vs galeries
 * @param {object} doc - même entrée que validateProductData (doit contenir doc.galeriesID et doc.galeries)
 * @param {string} url - URL candidate à retourner si la condition est vraie
 * @returns {string|undefined} - url si true, sinon le premier url de galeriesID (ou undefined)
 */
export function selectUrl_By_GaleriesCheck(doc, url) {
  const urls = extract_Urls_galeriesID(doc?.galeriesID);          // string[]
  const names = extract_Na_Ing_galeries(doc?.galeries).names;      // string[]

  const ok = compareArrays(urls, names); // true si every name ∈ urls
  return ok ? url : urls[0];
}
